import { useAppDispatch, useAppSelector } from 'app/state'
import useAccountSpatialExtensionInfo from 'features/account/state/useAccountSpatialExtensionInfo'
import { useCallback, useEffect } from 'react'
import { useMutation, useQuery } from 'react-query'
import {
  addSubscriptionRequested,
  checkIfDatasetHasBeenRequested,
  getActiveSubscriptions,
  getSubscribedSamples,
  getSubscriptionsRequested,
  removeSubscriptionRequested,
  subscribeDataset,
  subscribeSample
} from '../../api/subscriptions'

import { RequestStatus } from 'features/common'
import {
  setCurrentTable,
  setCurrentTableDataPreviewStatus,
  setCurrentTableStatus,
  setDataExplorerTreeStatus,
  setExpandedNodes
} from 'features/data-explorer/state/dataExplorerSlice'
import { setSubscriptionsStatus } from 'features/data-observatory/state/dataObservatorySlice'
import { useNavigate } from 'react-router-dom'
import {
  CatalogDataset,
  CatalogEntityTypes,
  CatalogSubscriptionRequestStatus,
  CatalogSubscriptionStatus,
  CatalogSubscriptionType
} from '../../types'
import { CatalogSubscriptionActionsContext, CatalogSubscriptionDataContext } from './context'
import { useConnections } from 'features/connections/hooks/connectionsHooks'
import { ConnectionType } from 'features/data-explorer/types'

const EXPIRATION_THRESHOLD_DAYS = 30

const retryOpts = {
  retry: 5,
  retryDelay: 1000
}

export function SubscriptionsProvider({
  children,
  publicView = false
}: {
  children: React.ReactNode
  publicView?: boolean
}) {
  // Due to a wrong connection management, it should be changed when connection sagas will be refactored
  useConnections({ refetchOnWindowFocus: false })

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const me = useAppSelector((state) => state.users.me)

  const { project, cartoDWAccountProject, dataProject, dataset, spatialExtensionConnection } =
    useAccountSpatialExtensionInfo(publicView)

  const { data: requested, refetch: refetchRequested } = useQuery({
    queryKey: ['get-requested-subscriptions'],
    queryFn: () => getSubscriptionsRequested(),
    keepPreviousData: true,
    enabled: !publicView
  })

  const {
    data: actives,
    isFetching: isSubscriptionsLoading,
    refetch: refetchActives
  } = useQuery({
    queryKey: ['get-active-subscriptions'],
    queryFn: () => getActiveSubscriptions(),
    keepPreviousData: true,
    enabled: !publicView,
    ...retryOpts
  })

  const getActiveSubscription = useCallback(
    (dataset: CatalogDataset) => {
      return actives?.find((d) => d.datasetId === dataset?.id)
    },
    [actives]
  )

  const {
    data: samples,
    isFetching: isSamplesLoading,
    refetch: refetchSamples
  } = useQuery<string[]>({
    queryKey: ['get-subscribed-samples'],
    keepPreviousData: true,
    queryFn: async () =>
      await getSubscribedSamples({
        connection: spatialExtensionConnection?.name as string,
        project,
        dataProject,
        dataset,
        refresh: true
      }),
    enabled: !publicView && Boolean(cartoDWAccountProject) && Boolean(spatialExtensionConnection?.name),
    ...retryOpts
  })

  useEffect(() => {
    const dirty = requested?.filter((r) => actives?.some((s) => s.datasetId === r))
    if (dirty?.length) {
      dirty.forEach((d) => {
        removeSubscriptionRequested({ dataset: d })
      })
      refetchRequested()
    }
  }, [actives, requested, refetchRequested])

  const getTableName = useCallback(
    (dataset: CatalogDataset, subscriptionType: CatalogSubscriptionType = CatalogSubscriptionType.DATA) => {
      if (subscriptionType === CatalogSubscriptionType.SAMPLE) {
        const source = String(dataset.sampleInfo?.id ?? '')
        const lastPart = source.split('.').pop() as string
        const sample = samples?.find((s) => s.includes(lastPart))
        return String(sample ?? '').replace(/\./g, '/')
      }
      const subscription = getActiveSubscription(dataset)
      const tableName = String(subscription?.tableName ?? '')
      return tableName.replace(/\./g, '/')
    },
    [getActiveSubscription, samples]
  )

  const invalidateDataExplorerTree = useCallback(
    (dataset: CatalogDataset, subscriptionType: CatalogSubscriptionType = CatalogSubscriptionType.DATA) => {
      const tableName = getTableName(dataset, subscriptionType)
      const connection = spatialExtensionConnection?.name || ConnectionType.CartoDw
      const accountId = me?.user_metadata?.account_id as string
      dispatch(setSubscriptionsStatus(RequestStatus.Idle))
      dispatch(setCurrentTableDataPreviewStatus(RequestStatus.Idle))
      dispatch(setCurrentTableStatus(RequestStatus.Idle))
      dispatch(setCurrentTable(null))
      dispatch(setDataExplorerTreeStatus(RequestStatus.Idle))
      dispatch(setExpandedNodes([connection, dataProject, accountId, tableName]))
    },
    [dispatch, getTableName, dataProject, spatialExtensionConnection?.name, me?.user_metadata?.account_id]
  )

  const requestAccessSampleMutation = useMutation((dataset: CatalogDataset) => subscribeSample({ dataset }), {
    onSuccess: async (_, _dataset) => {
      await refetchSamples()
      invalidateDataExplorerTree(_dataset)
    }
  })

  const requestSubscriptionMutation = useMutation(
    (params: {
      dataset: CatalogDataset
      requestStatus: CatalogSubscriptionRequestStatus
      email: string
      datasetType: CatalogEntityTypes
      hasCDWAccount: boolean
      accountName: string
    }) => subscribeDataset(params),
    {
      onSuccess: async (_, { dataset, requestStatus }) => {
        if (dataset.licenseId !== 'public' || !cartoDWAccountProject) {
          if (requestStatus === CatalogSubscriptionRequestStatus.ACTIVE) {
            addSubscriptionRequested({ dataset })
          } else if (requestStatus === CatalogSubscriptionRequestStatus.CANCEL) {
            removeSubscriptionRequested({ dataset })
          }
        }
        await refetchRequested()
        await refetchActives()
        invalidateDataExplorerTree(dataset)
      }
    }
  )

  const checkIfSubscriptionRequested = useCallback((dataset: CatalogDataset) => {
    return checkIfDatasetHasBeenRequested(dataset.id)
  }, [])

  const checkIfSubscribed = useCallback(
    (dataset: CatalogDataset) => {
      return Boolean(getActiveSubscription(dataset))
    },
    [getActiveSubscription]
  )

  const getSubscriptionExpiration = useCallback(
    (dataset: CatalogDataset) => {
      const exp = getActiveSubscription(dataset)?.expiresAt
      if (exp) {
        return new Date(exp)
      }
      return exp
    },
    [getActiveSubscription]
  )

  const getSubscriptionExpirationLeft = useCallback(
    (dataset: CatalogDataset) => {
      const expires = getSubscriptionExpiration(dataset)
      if (!expires) {
        return Number.MAX_SAFE_INTEGER
      }
      const today = new Date()
      const target = new Date(expires)
      today.setHours(0, 0, 0, 0)
      target.setHours(0, 0, 0, 0)
      const diff = target.getTime() - today.getTime()
      return Math.ceil(diff / (1000 * 3600 * 24))
    },
    [getSubscriptionExpiration]
  )

  const getSubscriptionStatus = useCallback(
    (dataset?: CatalogDataset) => {
      if (!dataset) {
        return null
      }
      if (getSubscriptionExpirationLeft(dataset) <= EXPIRATION_THRESHOLD_DAYS) {
        return CatalogSubscriptionStatus.EXPIRES_SOON
      }
      if (checkIfSubscribed(dataset)) {
        return CatalogSubscriptionStatus.SUBSCRIBED
      }

      if (checkIfSubscriptionRequested(dataset)) {
        return CatalogSubscriptionStatus.REQUESTED
      }

      return null
    },
    [checkIfSubscriptionRequested, getSubscriptionExpirationLeft, checkIfSubscribed]
  )

  const sendSubscriptionRequest = useCallback(
    (dataset: CatalogDataset, status: CatalogSubscriptionRequestStatus = CatalogSubscriptionRequestStatus.ACTIVE) => {
      const email = me?.email || ''
      const datasetType = dataset.isGeography ? CatalogEntityTypes.Geography : CatalogEntityTypes.Dataset
      return requestSubscriptionMutation.mutateAsync({
        dataset,
        requestStatus: status,
        email,
        datasetType,
        hasCDWAccount: Boolean(cartoDWAccountProject),
        accountName: me?.user_metadata?.account_name as string
      })
    },
    [requestSubscriptionMutation, me, cartoDWAccountProject]
  )

  const navigateToDataExplorer = useCallback(
    (dataset: CatalogDataset, subscriptionType: CatalogSubscriptionType = CatalogSubscriptionType.DATA) => {
      invalidateDataExplorerTree(dataset, subscriptionType)
      const tableName = getTableName(dataset, subscriptionType)
      const connection = spatialExtensionConnection?.name || ConnectionType.CartoDw
      const accountId = me?.user_metadata?.account_id
      const link = `/data/explorer/${connection}/${dataProject}/${accountId}/${tableName}`
      navigate(link)
    },
    [
      invalidateDataExplorerTree,
      navigate,
      getTableName,
      dataProject,
      spatialExtensionConnection?.name,
      me?.user_metadata?.account_id
    ]
  )

  const actions = {
    navigateToDataExplorer,
    sendSubscriptionRequest,
    checkIfSubscriptionRequested,
    checkIfSubscribed,
    getSubscriptionStatus,
    getSubscriptionExpirationLeft,
    getSubscriptionExpiration
  }

  const values = {
    cartoDWAccountProject,
    publicView,
    actives,
    samples,
    requested,
    isSubscriptionsLoading,
    isSamplesLoading,
    requestSubscriptionMutation: requestSubscriptionMutation,
    requestAccessSampleMutation: requestAccessSampleMutation
  }
  return (
    <CatalogSubscriptionActionsContext.Provider value={actions}>
      <CatalogSubscriptionDataContext.Provider value={values}>{children}</CatalogSubscriptionDataContext.Provider>
    </CatalogSubscriptionActionsContext.Provider>
  )
}
